// OutcomeAudioSynthesizer.ts - Procedural Web Audio synthesis for hand and match outcomes
import { AudioSynthHelper } from './AudioSynthHelper';

export class OutcomeAudioSynthesizer {
  /**
   * 5-tone descending sequence down the scale ("bo do do do dum")
   * Played when getting set (failing contract) or losing a match.
   */
  public static playDescending5ToneSet(ctx: AudioContext) {
    const t = ctx.currentTime + 0.02;

    const notes = [
      { freq: 392.00, start: 0.00, dur: 0.13, gain: 0.22, filterFreq: 800 }, // bo (G4)
      { freq: 349.23, start: 0.12, dur: 0.13, gain: 0.22, filterFreq: 800 }, // do (F4)
      { freq: 311.13, start: 0.24, dur: 0.13, gain: 0.22, filterFreq: 750 }, // do (Eb4)
      { freq: 293.66, start: 0.36, dur: 0.14, gain: 0.22, filterFreq: 700 }, // do (D4)
    ];

    notes.forEach((n) => {
      AudioSynthHelper.playTone(ctx, {
        freq: n.freq,
        start: t + n.start,
        dur: n.dur,
        gain: n.gain,
        type: 'triangle',
        attack: 0.008,
        filter: { type: 'lowpass', freq: n.filterFreq },
      });
    });

    // 5th step ("dum"): Deeper, resonant landing note dropping into sub-bass
    const dumTime = t + 0.49;
    AudioSynthHelper.playTone(ctx, {
      freq: 261.63, // C4
      endFreq: 130.81, // drops to low C3
      start: dumTime,
      dur: 0.35,
      gain: 0.32,
      type: 'triangle',
      attack: 0.010,
      filter: { type: 'lowpass', freq: 500, endFreq: 220 },
    });

    // Subtle low thud body for the landing "dum"
    AudioSynthHelper.playTone(ctx, {
      freq: 130.81,
      endFreq: 65.41,
      start: dumTime,
      dur: 0.25,
      gain: 0.25,
      type: 'sine',
      attack: 0.005,
    });
  }

  /**
   * 5-tone ascending sequence up the scale ("bo do do dum ding")
   * Played when making a bid, setting opponents, or winning a match.
   */
  public static playAscending5ToneSuccess(ctx: AudioContext) {
    const t = ctx.currentTime + 0.02;

    const steps = [
      { freq: 261.63, start: 0.00, dur: 0.11, gain: 0.20 }, // bo (C4)
      { freq: 293.66, start: 0.10, dur: 0.11, gain: 0.20 }, // do (D4)
      { freq: 329.63, start: 0.20, dur: 0.11, gain: 0.22 }, // do (E4)
      { freq: 392.00, start: 0.30, dur: 0.13, gain: 0.24 }, // dum (G4)
    ];

    steps.forEach((step) => {
      AudioSynthHelper.playTone(ctx, {
        freq: step.freq,
        start: t + step.start,
        dur: step.dur,
        gain: step.gain,
        type: 'triangle',
        attack: 0.006,
        filter: { type: 'lowpass', freq: 1600 },
      });
    });

    // 5th step ("ding" / "ping"): Pristine, crystal bell chime (pure sine harmonics, zero static, zero train horn)
    const dingTime = t + 0.42;

    // Layer 1: Pure crystal bell fundamental (C6) - clean, singing, resonant
    AudioSynthHelper.playTone(ctx, {
      freq: 1046.50, // C6
      start: dingTime,
      dur: 0.65,
      gain: 0.36,
      type: 'sine',
      attack: 0.002,
    });

    // Layer 2: Sweet bell fifth overtone (G6)
    AudioSynthHelper.playTone(ctx, {
      freq: 1567.98, // G6
      start: dingTime,
      dur: 0.45,
      gain: 0.14,
      type: 'sine',
      attack: 0.002,
    });

    // Layer 3: Silver shimmer octave (C7)
    AudioSynthHelper.playTone(ctx, {
      freq: 2093.00, // C7
      start: dingTime,
      dur: 0.30,
      gain: 0.08,
      type: 'sine',
      attack: 0.002,
    });

    // Layer 4: Pure-tone bell strike ping (micro-transient, NO white noise / static)
    AudioSynthHelper.playTone(ctx, {
      freq: 3136.00,
      endFreq: 2093.00,
      start: dingTime,
      dur: 0.010,
      gain: 0.06,
      type: 'sine',
      attack: 0.001,
    });
  }

  /**
   * Dramatic, heavy 2-tone doom ("dummmm dooooom") when match ends in defeat.
   */
  public static playMatchDefeatDoom(ctx: AudioContext) {
    const t = ctx.currentTime + 0.05;

    // Note 1: "dummmm" - Deep somber gong/thud (D2 -> C2)
    const dumTime = t;
    AudioSynthHelper.playTone(ctx, {
      freq: 73.42, // D2
      endFreq: 65.41, // C2
      start: dumTime,
      dur: 0.85,
      gain: 0.42,
      type: 'triangle',
      attack: 0.008,
      filter: { type: 'lowpass', freq: 320, endFreq: 120 },
    });

    // Sub-bass underlay for "dummmm"
    AudioSynthHelper.playTone(ctx, {
      freq: 36.71, // D1
      endFreq: 32.70, // C1
      start: dumTime,
      dur: 0.75,
      gain: 0.35,
      type: 'sine',
      attack: 0.010,
    });

    // Note 2: "dooooom" - Massive, reverberant dark doom gong plunging into the sub-bass abyss
    const doomTime = t + 0.65;
    AudioSynthHelper.playTone(ctx, {
      freq: 55.00, // A1
      endFreq: 27.50, // A0 (extreme deep sub-bass plunge)
      start: doomTime,
      dur: 1.60,
      gain: 0.48,
      type: 'triangle',
      attack: 0.012,
      filter: { type: 'lowpass', freq: 240, endFreq: 80 },
    });

    // Sub-bass seismic body for "dooooom"
    AudioSynthHelper.playTone(ctx, {
      freq: 32.70, // C1
      endFreq: 24.50, // G0
      start: doomTime,
      dur: 1.40,
      gain: 0.40,
      type: 'sine',
      attack: 0.015,
    });

    // Dark minor overtone chime
    AudioSynthHelper.playTone(ctx, {
      freq: 110.00, // A2
      endFreq: 55.00,
      start: doomTime,
      dur: 1.10,
      gain: 0.16,
      type: 'sine',
      attack: 0.010,
      filter: { type: 'lowpass', freq: 400 },
    });
  }

  /**
   * Sparkling arcade / game-show jackpot prize tone for Bostons.
   * Played when any team (especially the player) achieves a Boston.
   */
  public static playBostonPrizeTone(ctx: AudioContext) {
    const t = ctx.currentTime + 0.02;

    // Rapid ascending arpeggiated sparkle
    const arpeggioNotes = [
      { freq: 523.25, start: 0.00, dur: 0.09, gain: 0.18 },  // C5
      { freq: 659.25, start: 0.06, dur: 0.09, gain: 0.18 },  // E5
      { freq: 783.99, start: 0.12, dur: 0.10, gain: 0.20 },  // G5
      { freq: 1046.50, start: 0.18, dur: 0.11, gain: 0.22 }, // C6
      { freq: 1318.51, start: 0.24, dur: 0.13, gain: 0.24 }, // E6
      { freq: 1567.98, start: 0.30, dur: 0.50, gain: 0.26 }, // G6
      { freq: 2093.00, start: 0.32, dur: 0.55, gain: 0.24 }, // C7
    ];

    arpeggioNotes.forEach((n) => {
      AudioSynthHelper.playTone(ctx, {
        freq: n.freq,
        start: t + n.start,
        dur: n.dur,
        gain: n.gain,
        type: 'sine',
        attack: 0.003,
      });
    });

    // Ringing major prize chord on arrival (C6, E6, G6, C7)
    const chordTime = t + 0.30;
    const prizeChord = [1046.50, 1318.51, 1567.98, 2093.00];
    prizeChord.forEach((freq, idx) => {
      AudioSynthHelper.playTone(ctx, {
        freq,
        start: chordTime + idx * 0.012,
        dur: 0.65 - idx * 0.05,
        gain: 0.22 / (idx * 0.4 + 1),
        type: 'triangle',
        attack: 0.003,
        filter: { type: 'lowpass', freq: 3800 },
      });
    });

    // Shimmering air / celebration noise burst
    AudioSynthHelper.playNoiseBurst(ctx, {
      start: chordTime,
      dur: 0.45,
      decay: 0.25,
      gain: 0.14,
      attack: 0.015,
      filter: { type: 'bandpass', freq: 3000, Q: 2.2 },
    });
  }

  /**
   * Authentic classic steam locomotive whistle ("toot toot!").
   * Played when the Boston celebratory train rolls across the screen.
   */
  public static playTrainWhistle(ctx: AudioContext) {
    const t = ctx.currentTime + 0.05;
    // Classic American steam whistle chime (major triad: Eb5, G5, Bb5)
    const toots = [
      { start: 0.00, dur: 0.26 },
      { start: 0.36, dur: 0.52 },
    ];
    const freqs = [622.25, 783.99, 932.33];

    toots.forEach((toot) => {
      freqs.forEach((f, idx) => {
        AudioSynthHelper.playTone(ctx, {
          freq: f,
          start: t + toot.start,
          dur: toot.dur,
          gain: 0.12 / (idx * 0.35 + 1),
          type: 'triangle',
          attack: 0.035,
          filter: { type: 'bandpass', freq: f, Q: 8 },
        });
        // Resonant harmonic overtone
        AudioSynthHelper.playTone(ctx, {
          freq: f * 2,
          start: t + toot.start,
          dur: toot.dur * 0.8,
          gain: 0.04 / (idx * 0.4 + 1),
          type: 'sine',
          attack: 0.03,
        });
      });
      // Breathy steam hiss layer
      AudioSynthHelper.playNoiseBurst(ctx, {
        start: t + toot.start,
        dur: toot.dur,
        decay: toot.dur * 0.45,
        gain: 0.04,
        attack: 0.03,
        filter: { type: 'bandpass', freq: 1500, Q: 2 },
      });
    });
  }

  /**
   * Ethereal, celestial cosmic chime sequence for Hearts "Shoot the Moon".
   * Features ascending crystal bell arpeggio and a resonant stellar shimmer chord.
   */
  public static playShootTheMoon(ctx: AudioContext) {
    const t = ctx.currentTime + 0.05;

    // Celestial rising arpeggio (C5 -> E5 -> G5 -> B5 -> D6 -> G6)
    const moonNotes = [
      { freq: 523.25, start: 0.00, dur: 0.12, gain: 0.16 }, // C5
      { freq: 659.25, start: 0.08, dur: 0.13, gain: 0.18 }, // E5
      { freq: 783.99, start: 0.16, dur: 0.14, gain: 0.20 }, // G5
      { freq: 987.77, start: 0.24, dur: 0.15, gain: 0.22 }, // B5
      { freq: 1174.66, start: 0.32, dur: 0.18, gain: 0.24 }, // D6
      { freq: 1567.98, start: 0.40, dur: 0.65, gain: 0.26 }, // G6
    ];

    moonNotes.forEach((n) => {
      AudioSynthHelper.playTone(ctx, {
        freq: n.freq,
        start: t + n.start,
        dur: n.dur,
        gain: n.gain,
        type: 'sine',
        attack: 0.003,
      });
    });

    // Radiant lunar shimmer chord on peak arrival (Eb5, G5, Bb5, D6, F6)
    const chordTime = t + 0.42;
    const lunarChord = [622.25, 783.99, 932.33, 1174.66, 1396.91];
    lunarChord.forEach((freq, idx) => {
      AudioSynthHelper.playTone(ctx, {
        freq,
        start: chordTime + idx * 0.015,
        dur: 0.95 - idx * 0.05,
        gain: 0.24 / (idx * 0.35 + 1),
        type: 'sine',
        attack: 0.004,
        filter: { type: 'lowpass', freq: 4500 },
      });
    });

    // Cosmic ether shimmer noise layer
    AudioSynthHelper.playNoiseBurst(ctx, {
      start: chordTime,
      dur: 0.65,
      decay: 0.45,
      gain: 0.08,
      attack: 0.02,
      filter: { type: 'bandpass', freq: 3500, Q: 3.5 },
    });
  }
}